import { faCamera } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import React from "react";

import {
    aboutPrivacyPolicyUrl,
    aboutRulesCommonUrl,
    aboutRulesMarketUrl,
    mamaletoPrivacyPolicyLinkLabel,
    mamaletoPrivacyPolicyLinkUrl,
    modrykonikPrivacyPolicyLinkLabel,
} from "@/mk2/apps/about/urls";
import { PregnancyState, SexType } from "@/mk2/apps/settings/schemas";
import {
    FOREIGN_COUNTRIES_PHONE_PREFIXES_OPTIONS,
    HOME_COUNTRY_PHONE_PREFIX,
} from "@/mk2/apps/users/constants";
import {
    SIGNUP_PHONE_REQUEST_TOKEN_TRIGGER,
    SIGNUP_PHONE_VALIDATE_TOKEN_TRIGGER,
    SIGNUP_VALIDATE_REGISTRATION_INPUTS_TRIGGER,
} from "@/mk2/apps/users/containers/Signup/Signup.actions";
import { SIGNUP_FORM_NAME } from "@/mk2/apps/users/containers/Signup/Signup.constants";
import { QueryParams } from "@/mk2/apps/users/containers/Signup/SignupPage";
import SignupProgressBar from "@/mk2/apps/users/containers/Signup/SignupProgressBar";
import { loginUrl } from "@/mk2/apps/users/urls";
import {
    getGenericAvatar,
    gtmEvent,
    hasValidationViaSms,
    prettyNumber,
} from "@/mk2/apps/users/utils";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { Img } from "@/mk2/components/Img";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { DateSelectField } from "@/mk2/components/forms/DateSelectField";
import { InputField } from "@/mk2/components/forms/InputField";
import { PasswordField } from "@/mk2/components/forms/PasswordField";
import { PhotoCropField } from "@/mk2/components/forms/PhotoCropField";
import { PhotosFieldNew } from "@/mk2/components/forms/PhotosFieldNew";
import { Option, SelectField } from "@/mk2/components/forms/SelectField";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { FormPageType } from "@/mk2/constants/enums";
import ActionButton from "@/mk2/containers/ActionButton/ActionButton";
import { PhotosUploadConfig } from "@/mk2/containers/PhotosUpload/PhotosUpload";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import { GetParams, url } from "@/mk2/helpers/urls";
import { PhotoEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import {
    ADD_AVATAR,
    AVATAR_PHOTO_RULES_LINE1,
    AVATAR_PHOTO_RULES_LINE2,
    AVATAR_PHOTO_RULES_LINE3,
    AVATAR_PHOTO_RULES_LINE4,
    AVATAR_PHOTO_RULES_TITLE,
    DO_NOT_WANT_TO_TELL,
    HOME_SIGNUP_BAZAAR,
    HOME_SIGNUP_IF_REGISTERED_LOGIN_HERE_link,
    HOME_SIGNUP_I_AGREE_WITH_SITE_RULES_bazaar_link_common_link_privacy_link,
    HOME_SIGNUP_LOGIN_HERE,
    HOME_SIGNUP_PRIVACY,
    HOME_SIGNUP_VOP,
    PREGNANCY_STATE,
    PREGNANT,
    SIGNUP_CONFIRM_AND_SIGN_UP,
    SIGNUP_DUE_DATE,
    SIGNUP_EMAIL,
    SIGNUP_I_DONT_WANT_UPLOAD_PHOTO,
    SIGNUP_LOCATION_COUNTRY,
    SIGNUP_LOCATION_COUNTY,
    SIGNUP_LOCATION_PLACE,
    SIGNUP_NEXT_STEP,
    SIGNUP_PASSWORD,
    SIGNUP_PHONE_ANSWER,
    SIGNUP_PHONE_CALL_ME_CODE,
    SIGNUP_PHONE_CHANGE_NUMBER_BTN,
    SIGNUP_PHONE_CONTACTING_YOU_ON_number_change,
    SIGNUP_PHONE_COUNTRY_PREFIX,
    SIGNUP_PHONE_EXPLANATION_CALL_price,
    SIGNUP_PHONE_EXPLANATION_FREE,
    SIGNUP_PHONE_EXPLANATION_SMS_price,
    SIGNUP_PHONE_I_HAVE_FOREIGN_PHONE_NUMBER,
    SIGNUP_PHONE_I_HAVE_LOCAL_PHONE_NUMBER,
    SIGNUP_PHONE_QUESTION,
    SIGNUP_PHONE_SEND_ME_CODE,
    SIGNUP_PHONE_TITLE,
    SIGNUP_PHONE_TOKEN_CALL_LABEL,
    SIGNUP_PHONE_TOKEN_NOT_RECEIVED_RESEND_CALL,
    SIGNUP_PHONE_TOKEN_NOT_RECEIVED_RESEND_SMS,
    SIGNUP_PHONE_TOKEN_SMS_LABEL,
    SIGNUP_PHONE_VALIDATE_TOKEN,
    SIGNUP_PHONE_WE_SENT_YOU_ACTIVATION_CODE_CALL,
    SIGNUP_PHONE_WE_SENT_YOU_ACTIVATION_CODE_SMS,
    SIGNUP_PHONE_YOUR_LOCAL_PHONE_NUMBER,
    SIGNUP_PHONE_YOUR_MOBILE_NUMBER,
    SIGNUP_UPLOAD_ANOTHER_PHOTO,
    SIGNUP_USERNAME,
    SIGNUP_WOULD_YOU_LIKE_TO_UPLOAD_PHOTO,
    WE_ARE_TRYING_TO_CONCEIVE,
    WE_HAVE_CHILDREN,
} from "@/mk/autogenerated/translation";
import { countiesSelectOptions, getPlacesSelectOptionsForCounty } from "@/mk/geo";

import styles from "./SignupForm.mscss";

interface OwnProps {
    isMobile: boolean;
    query: QueryParams;
    initialValues: SignupFormData;
    currentValues: SignupFormData;
    avatar?: PhotoEntity;
    location: HistoryLocationWithState;
    onStoreEvent?(name: string, props: any);
    onSignup(values: SignupFormData);
    onValidateInputs?(values: SignupFormData);
    onPhoneVerificationRequestToken?(
        countryPrefix: string,
        rawPhoneNumber: string,
        resend: boolean,
        location: HistoryLocationWithState,
    );
    onPhoneVerificationValidateToken?(
        token: string,
        fullPhoneNumber: string,
        location: HistoryLocationWithState,
    );
    onPrepareUploadPhotos?(
        formName: string,
        config: PhotosUploadConfig,
        files: File[],
        processPhotoUploadOnFormPage: FormPageType,
        accessKey?: string,
    );
}

interface OwnState {
    showDueDate: boolean;
    countyId: number;
    foreignNumber: boolean;
    crop: any;
}

export interface SignupFormData {
    username: string;
    password: string;
    email: string;
    locationCounty?: string;
    place?: string;
    pregnancyState?: string;
    dueDate?: string;
    countryPrefix: string;
    rawPhoneNumber: string; // cislo co zadal uzivatel, mozno s 0 navyse na zaciatku, napr. 0903707656
    fullPhoneNumber: string; // cislo normalizovne na backende s country prefixom, napr. +421903707656
    token: string;
    photos: any;
    currentPhoto: boolean;
    visibleFace: boolean;
    alonePhoto: boolean;
    avatarCrop: any;
}

const photosConfig: PhotosUploadConfig = {
    photoType: "avatar",
    multiple: false,
    showPhotoCode: false,
};

type Props = OwnProps;

const PREGNANCY_STATE_CHOICES: Option[] = [
    { value: null, label: "", isPlaceholder: true },
    { value: PregnancyState.Pregnant, label: PREGNANT },
    { value: PregnancyState.Trying, label: WE_ARE_TRYING_TO_CONCEIVE },
    { value: PregnancyState.HasChildren, label: WE_HAVE_CHILDREN }, // FYI, the translation WE_HAVE_CHILDREN has value "No", but it's correct
    { value: PregnancyState.DoNotTell, label: DO_NOT_WANT_TO_TELL },
];

class SignupFormComponent extends React.Component<
    Props & MKReduxFormInjectedProps<SignupFormData, Props>,
    OwnState
> {
    public state: OwnState = {
        showDueDate: this.props.initialValues.pregnancyState === "1",
        countyId: -1,
        foreignNumber: false,
        crop: {
            width: 100,
            aspect: 1,
        },
    };

    private photosFieldRef: PhotosFieldNew;

    public componentDidMount() {
        gtmEvent("event_signup_visited");
        document.addEventListener("keyup", this.handleOnKeyPressed);
    }

    public componentWillUnmount() {
        document.removeEventListener("keyup", this.handleOnKeyPressed);
    }

    public render() {
        const { currentFormPage } = this.props;
        if (currentFormPage === FormPageType.SignupPhotoUpload) {
            return this.renderPhotoUpload();
        } else if (currentFormPage === FormPageType.SignupTokenValidation) {
            return this.renderTokenValidation();
        } else if (currentFormPage === FormPageType.SignupPhoneVerification) {
            return this.renderPhoneVerification();
        } else {
            return this.renderMain();
        }
    }

    public renderMain() {
        // step 1
        const { currentFormPage, handleSubmit, isMobile, query } = this.props;

        return (
            <>
                <div
                    className={cx(
                        styles.SignupForm,
                        currentFormPage === FormPageType.Main && styles[`SignupForm--mainPage`],
                    )}
                >
                    <div>
                        <Form className={styles.SignupForm__form} onSubmit={handleSubmit}>
                            <InputField
                                name="username"
                                type="text"
                                label={SIGNUP_USERNAME}
                                autoComplete="new-password"
                            />
                            <PasswordField
                                name="password"
                                label={SIGNUP_PASSWORD}
                                autoComplete="new-password"
                            />
                            <InputField name="email" type="text" label={SIGNUP_EMAIL} />
                            <SelectField
                                name="locationCounty"
                                label={SIGNUP_LOCATION_COUNTY}
                                options={countiesSelectOptions}
                                onChange={this.handleSelectCounty}
                            />
                            {this.state.countyId >= 0 ? (
                                <SelectField
                                    name="place"
                                    options={this.placesOptions()}
                                    label={
                                        this.state.countyId > 0
                                            ? SIGNUP_LOCATION_PLACE
                                            : SIGNUP_LOCATION_COUNTRY
                                    }
                                />
                            ) : null}
                            <SelectField
                                name="pregnancyState"
                                label={PREGNANCY_STATE}
                                options={PREGNANCY_STATE_CHOICES}
                                onChange={this.handlePregnancyStateChange}
                            />
                            {this.state.showDueDate && (
                                <DateSelectField
                                    name="dueDate"
                                    label={SIGNUP_DUE_DATE}
                                    yearFrom={new Date().getFullYear()}
                                    yearTo={
                                        new Date().getMonth() > 3
                                            ? new Date().getFullYear() + 1
                                            : new Date().getFullYear()
                                    }
                                />
                            )}
                            <div className={styles.SignupForm__buttonWrapper}>
                                <ActionButton
                                    className={styles.SignupForm__button}
                                    label={SIGNUP_NEXT_STEP}
                                    triggerAction={SIGNUP_VALIDATE_REGISTRATION_INPUTS_TRIGGER}
                                    onClick={this.handleOnNextStep}
                                    showSpinnerWhenBusy
                                />

                                <p className={styles.SignupForm__signin}>
                                    <Interpolate
                                        i18nKey={HOME_SIGNUP_IF_REGISTERED_LOGIN_HERE_link}
                                        link={
                                            <Link
                                                data-google-interstitial="false"
                                                to={url(
                                                    loginUrl,
                                                    {},
                                                    query as unknown as GetParams,
                                                )}
                                            >
                                                {HOME_SIGNUP_LOGIN_HERE}
                                            </Link>
                                        }
                                    />
                                </p>
                            </div>
                        </Form>
                    </div>
                    {!isMobile && (
                        <div className={styles.SignupForm__textWrapper}>
                            <p className={styles.SignupForm__secondaryText}>
                                <Interpolate
                                    i18nKey={
                                        HOME_SIGNUP_I_AGREE_WITH_SITE_RULES_bazaar_link_common_link_privacy_link
                                    }
                                    bazaar_link={
                                        <Link
                                            data-google-interstitial="false"
                                            to={aboutRulesMarketUrl}
                                        >
                                            {HOME_SIGNUP_BAZAAR}
                                        </Link>
                                    }
                                    common_link={
                                        <Link
                                            data-google-interstitial="false"
                                            to={aboutRulesCommonUrl}
                                        >
                                            {HOME_SIGNUP_VOP}
                                        </Link>
                                    }
                                    privacy_link={
                                        <>
                                            <Link
                                                data-google-interstitial="false"
                                                to={aboutPrivacyPolicyUrl}
                                            >
                                                {modrykonikPrivacyPolicyLinkLabel}
                                            </Link>{" "}
                                            a{" "}
                                            <Link
                                                data-google-interstitial="false"
                                                to={mamaletoPrivacyPolicyLinkUrl}
                                            >
                                                {mamaletoPrivacyPolicyLinkLabel}
                                            </Link>
                                        </>
                                    }
                                />
                            </p>
                        </div>
                    )}
                </div>
            </>
        );
    }

    public renderPhoneVerification() {
        // step 2
        const { currentValues } = this.props;

        return (
            <div className={styles.SignupForm}>
                <div>
                    <div className={styles.SignupForm__svgWrapper}>
                        <SignupProgressBar lastCircleActive={false} />
                    </div>
                    <div className={styles.SignupForm__textWrapper}>
                        <h1 className={styles.SignupForm__title}>{SIGNUP_PHONE_TITLE}</h1>
                        <p className={styles.SignupForm__text}>
                            <Interpolate
                                i18nKey={
                                    !this.state.foreignNumber ||
                                    hasValidationViaSms(currentValues.countryPrefix)
                                        ? SIGNUP_PHONE_EXPLANATION_SMS_price
                                        : SIGNUP_PHONE_EXPLANATION_CALL_price
                                }
                                price={<strong>{SIGNUP_PHONE_EXPLANATION_FREE}</strong>}
                            />
                        </p>
                    </div>
                    {this.state.foreignNumber && (
                        <SelectField
                            name="countryPrefix"
                            label={SIGNUP_PHONE_COUNTRY_PREFIX}
                            options={FOREIGN_COUNTRIES_PHONE_PREFIXES_OPTIONS}
                            onChange={this.handleSelectCounty}
                        />
                    )}
                    <InputField
                        name="rawPhoneNumber"
                        type="text"
                        label={
                            !this.state.foreignNumber
                                ? SIGNUP_PHONE_YOUR_LOCAL_PHONE_NUMBER
                                : SIGNUP_PHONE_YOUR_MOBILE_NUMBER
                        }
                    />
                    <div className={styles.SignupForm__phoneActions}>
                        <ActionButton
                            key="send-token-btn"
                            type={BtnType.Blue}
                            label={
                                !this.state.foreignNumber ||
                                hasValidationViaSms(currentValues.countryPrefix)
                                    ? SIGNUP_PHONE_SEND_ME_CODE
                                    : SIGNUP_PHONE_CALL_ME_CODE
                            }
                            onClick={this.handleOnNextStep}
                            triggerAction={SIGNUP_PHONE_REQUEST_TOKEN_TRIGGER}
                            showSpinnerWhenBusy
                        />
                        <Btn
                            key="foreign-number-btn"
                            onClick={this.onForeignNumberClick}
                            label={
                                !this.state.foreignNumber
                                    ? SIGNUP_PHONE_I_HAVE_FOREIGN_PHONE_NUMBER
                                    : SIGNUP_PHONE_I_HAVE_LOCAL_PHONE_NUMBER
                            }
                            sidePadding={BtnSidePadding.None}
                            type={BtnType.BlueTextLight}
                            classNames={{
                                label: styles.SignupForm__secondaryBtn,
                            }}
                        />
                    </div>
                </div>
                <div className={cx(styles.SignupForm__textWrapper, styles.SignupForm__bottomText)}>
                    <h3 className={styles.SignupForm__secondaryTitle}>{SIGNUP_PHONE_QUESTION}</h3>
                    <p className={styles.SignupForm__secondaryText}>{SIGNUP_PHONE_ANSWER}</p>
                </div>
            </div>
        );
    }

    public renderTokenValidation() {
        // step 3
        const { currentValues } = this.props;

        return (
            <div className={styles.SignupForm}>
                <div>
                    <div className={styles.SignupForm__svgWrapper}>
                        <SignupProgressBar lastCircleActive={false} />
                    </div>
                    <div className={styles.SignupForm__textWrapper}>
                        <h1 className={styles.SignupForm__title}>
                            {hasValidationViaSms(currentValues.fullPhoneNumber)
                                ? SIGNUP_PHONE_WE_SENT_YOU_ACTIVATION_CODE_SMS
                                : SIGNUP_PHONE_WE_SENT_YOU_ACTIVATION_CODE_CALL}
                        </h1>
                        <p className={styles.SignupForm__text}>
                            <Interpolate
                                i18nKey={SIGNUP_PHONE_CONTACTING_YOU_ON_number_change}
                                number={
                                    <strong>{prettyNumber(currentValues.fullPhoneNumber)}</strong>
                                }
                                change={
                                    <Btn
                                        label={SIGNUP_PHONE_CHANGE_NUMBER_BTN}
                                        onClick={this.handleOnStepBack}
                                        sidePadding={BtnSidePadding.None}
                                        type={BtnType.BlueTextLight}
                                    />
                                }
                            />
                        </p>
                    </div>
                    <InputField
                        name="token"
                        type="text"
                        label={
                            hasValidationViaSms(currentValues.fullPhoneNumber)
                                ? SIGNUP_PHONE_TOKEN_SMS_LABEL
                                : SIGNUP_PHONE_TOKEN_CALL_LABEL
                        }
                    />
                    <div className={styles.SignupForm__phoneActions}>
                        <ActionButton
                            type={BtnType.Blue}
                            label={SIGNUP_PHONE_VALIDATE_TOKEN}
                            onClick={this.handleOnNextStep}
                            triggerAction={SIGNUP_PHONE_VALIDATE_TOKEN_TRIGGER}
                            showSpinnerWhenBusy
                        />
                        <Btn
                            onClick={this.handleResendVerificationToken}
                            label={
                                hasValidationViaSms(currentValues.fullPhoneNumber)
                                    ? SIGNUP_PHONE_TOKEN_NOT_RECEIVED_RESEND_SMS
                                    : SIGNUP_PHONE_TOKEN_NOT_RECEIVED_RESEND_CALL
                            }
                            sidePadding={BtnSidePadding.None}
                            type={BtnType.BlueTextLight}
                            classNames={{
                                label: styles.SignupForm__secondaryBtn,
                            }}
                        />
                    </div>
                </div>
                <div className={cx(styles.SignupForm__textWrapper, styles.SignupForm__bottomText)}>
                    <h3 className={styles.SignupForm__secondaryTitle}>{SIGNUP_PHONE_QUESTION}</h3>
                    <p className={styles.SignupForm__secondaryText}>{SIGNUP_PHONE_ANSWER}</p>
                </div>
            </div>
        );
    }

    public renderPhotoUpload() {
        // step 4
        const { avatar, isMobile } = this.props;
        return (
            <div className={cx(styles.SignupForm, styles.SignupForm__valignTop)}>
                <div className={styles.SignupForm__svgWrapper}>
                    <SignupProgressBar lastCircleActive />
                </div>
                {/*this has to be always rendered, so that <input type="file"> is in dom*/}
                {/*and we can call it's click() to show file open dialog*/}
                <PhotosFieldNew
                    key="photos"
                    name="photos"
                    config={photosConfig}
                    ref={this.handlePhotosFileInputFieldRef}
                    onUploadFiles={this.handleUploadFiles}
                />
                {!avatar && (
                    <div key="photo-upload-content">
                        <div className={styles.SignupForm__textWrapper}>
                            <h1 className={styles.SignupForm__title}>
                                {SIGNUP_WOULD_YOU_LIKE_TO_UPLOAD_PHOTO}
                            </h1>
                        </div>
                        <div className={styles.SignupForm__uploadAvatar}>
                            <Img
                                className={cx(
                                    styles.ProfileForm__avatarField__img,
                                    styles.SignupForm__opaqueAvatarImg,
                                )}
                                src={getGenericAvatar(150, SexType.Girl)}
                                width={100}
                                height={100}
                            />
                            <Btn
                                type={BtnType.Blue}
                                label={ADD_AVATAR}
                                icon={faCamera}
                                onClick={this.handleOpenFileExplorer}
                                className={styles.SignupForm__uploadAvatarBtn}
                            />
                            <SubmitButton
                                label={SIGNUP_I_DONT_WANT_UPLOAD_PHOTO}
                                formName={SIGNUP_FORM_NAME}
                                btnType={BtnType.BlueText}
                                className={styles.SignupForm__submitWithoutPhoto}
                            />
                        </div>
                    </div>
                )}

                {avatar && (
                    <div className={styles.SignupForm__cropAvatar}>
                        <div className={styles.SignupForm__cropWrapper}>
                            <PhotoCropField
                                name="avatarCrop"
                                photo={avatar}
                                marginLeft={false}
                                bottomBorder={false}
                            />
                        </div>
                        <Btn
                            label={SIGNUP_UPLOAD_ANOTHER_PHOTO}
                            type={BtnType.BlueText}
                            className={styles.SignupForm__uploadAnotherPhoto}
                            onClick={this.handleOpenFileExplorer}
                        />
                        <div className={styles.SignupForm__textWrapper}>
                            <h3
                                className={cx(
                                    styles.SignupForm__title,
                                    styles.SignupForm__photoRulesTitle,
                                )}
                            >
                                {AVATAR_PHOTO_RULES_TITLE}
                            </h3>
                        </div>
                        <ul className={styles.SignupForm__photoRules}>
                            <li>{AVATAR_PHOTO_RULES_LINE1}</li>
                            <li>{AVATAR_PHOTO_RULES_LINE2}</li>
                            <li>{AVATAR_PHOTO_RULES_LINE3}</li>
                            <li>{AVATAR_PHOTO_RULES_LINE4}</li>
                        </ul>
                    </div>
                )}

                {avatar && !isMobile && (
                    <SubmitButton
                        className={styles.SignupForm__submitBtn}
                        label={SIGNUP_CONFIRM_AND_SIGN_UP}
                        formName={SIGNUP_FORM_NAME}
                    />
                )}
            </div>
        );
    }

    private handlePhotosFileInputFieldRef = (ref: PhotosFieldNew) => {
        this.photosFieldRef = ref;
    };

    private handleOpenFileExplorer = () => {
        this.photosFieldRef.openFileExplorer();
    };

    private handleUploadFiles = (config: PhotosUploadConfig, files: File[]) => {
        const { onPrepareUploadPhotos, onStoreEvent } = this.props;
        onPrepareUploadPhotos(SIGNUP_FORM_NAME, config, files, FormPageType.SignupPhotoUpload);
        gtmEvent("event_signup_avatar_upload");
        onStoreEvent("signup_avatar_upload", {});
    };

    private handleResendVerificationToken = () => {
        const { onPhoneVerificationRequestToken, currentValues, location } = this.props;
        onPhoneVerificationRequestToken(
            !this.state.foreignNumber ? HOME_COUNTRY_PHONE_PREFIX : currentValues.countryPrefix,
            currentValues.rawPhoneNumber,
            true,
            location,
        );
    };

    private onForeignNumberClick = () => {
        this.setState({ foreignNumber: !this.state.foreignNumber });
    };

    private handleOnNextStep = () => {
        const {
            onValidateInputs,
            currentValues,
            onPhoneVerificationRequestToken,
            onPhoneVerificationValidateToken,
            currentFormPage,
            location,
        } = this.props;
        if (currentFormPage === FormPageType.Main) {
            onValidateInputs(currentValues);
        } else if (currentFormPage === FormPageType.SignupPhoneVerification) {
            onPhoneVerificationRequestToken(
                !this.state.foreignNumber ? HOME_COUNTRY_PHONE_PREFIX : currentValues.countryPrefix,
                currentValues.rawPhoneNumber,
                false,
                location,
            );
        } else if (currentFormPage === FormPageType.SignupTokenValidation) {
            onPhoneVerificationValidateToken(
                currentValues.token,
                currentValues.fullPhoneNumber,
                location,
            );
        }
    };

    private handleOnStepBack = () => {
        const { onValidateInputs, currentValues, currentFormPage } = this.props;
        if (currentFormPage === FormPageType.SignupTokenValidation) {
            onValidateInputs(currentValues);
        }
    };

    private handleSelectCounty = (event) => {
        this.setState({ countyId: parseInt(event.currentTarget.value, 10) });
    };

    private handlePregnancyStateChange = (event) => {
        if (event.currentTarget.value === "1") {
            this.setState({ showDueDate: true });
        } else {
            this.setState({ showDueDate: false });
        }
    };

    private placesOptions = () => {
        return getPlacesSelectOptionsForCounty(this.state.countyId.toString());
    };

    private handleOnKeyPressed = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" && e.which === 13) {
            e.stopPropagation();
            e.preventDefault();
            this.handleOnNextStep();
        }
    };
}

export const SignupForm = mkReduxForm<SignupFormData, Props>({
    formName: SIGNUP_FORM_NAME,
    onValidate: () => ({
        /* nothing to do, always valid */
    }),
    onSave: (values, props) => props.onSignup(values),
    disableDirtyCheck: true,
})(SignupFormComponent);
